import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import api from "../../../../Services/api";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { toast, ToastContainer } from "react-toastify";
import { useKeycloak } from "@react-keycloak/web";

import {
  keycloakbaseurl,
  createRole,
  updateRole,
  createRoleintoRealm,
  deletePermissionsFromRole,
  getAllPermissions,
  getRoleDataByRoleNameURL,
  addPermissionsToRole,
  getAllPermissionsMobile,
} from "../../../../Services/EndPoints";
import {
  CreateRoleConfig,
  schema,
} from "../../../../JsonData/CreateRoleConfig";
import {
  postDataWithBody,
  getData,
  putDataWithBody,
} from "../../../../Services/Services";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

// ---------------------------------------------------------------------------
// Helpers (defined outside component so they are stable references)
// ---------------------------------------------------------------------------
const MENU_ORDER = [
  "Dashboard",
  "Report",
  "My Task",
  "Administrative Activities",
  "Planning",
  "Situational Awareness/GIS",
  "Response and Relief",
];

function sortByMenuOrder(nodes) {
  return nodes
    .sort((a, b) => {
      const idxA = MENU_ORDER.findIndex(
        (n) => n.toLowerCase() === a.name?.toLowerCase()
      );
      const idxB = MENU_ORDER.findIndex(
        (n) => n.toLowerCase() === b.name?.toLowerCase()
      );
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    })
    .map((node) => ({
      ...node,
      children: node.children ? sortByMenuOrder(node.children) : [],
    }));
}

function flatToHier(flat) {
  const roots = [];
  const all = {};

  flat.forEach((item) => {
    all[item.id] = { ...item, label: item.name, value: item.id };
  });

  Object.values(all).forEach((item) => {
    if (item.parent_id == null) {
      roots.push(item);
    } else if (all[item.parent_id]) {
      const parent = all[item.parent_id];
      if (!parent.children) parent.children = [];
      parent.children.push(item);
    }
  });

  return sortByMenuOrder(roots);
}

function addSelectedFlag(data, selectedValues) {
  return data.map(({ children = [], ...rest }) => {
    const node = { ...rest };
    if (selectedValues.includes(node.id)) node.checked = true;
    if (children.length) node.children = addSelectedFlag(children, selectedValues);
    return node;
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RoleRegistrationForm = () => {
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();

  const dataFrEdit = state?.data;

  // ── form state ────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const [roleConfig, setRoleConfig] = useState(CreateRoleConfig);
  const [initDataLoadComplete, setInitDataLoadComplete] = useState(false);
  const [mobilePermissionsReady, setMobilePermissionsReady] = useState(false);

  // permission tracking
  // eslint-disable-next-line no-unused-vars
  const [rolePermissionData, setRolePermissionData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [permissionDataFrmService, setPermissionDataFrmService] = useState([]);
  const [permissionDataFrmServiceMobile, setPermissionDataFrmServiceMobile] =
    useState([]);

  // field disabled map
  const [isFieldDisabled, setIsFieldDisabled] = useState({
    web_permission: false,
    Web_access: false,
    Mobile_permission: false,
    Mobile_access: false,
  });

  // Key incremented to force full remount of the tree component when web is cleared.
  // react-dropdown-tree-select owns its own internal state, so a key change is the
  // only reliable way to reset it to zero selections.
  // eslint-disable-next-line no-unused-vars
  const [webTreeKey, setWebTreeKey] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [mobileKey, setMobileKey] = useState(0);

  // Ref: hidden 3rd-level children map { parentId: [childId, ...] }
  const hiddenChildrenMap = useRef({});
  // Ref: pristine (no checked flags) web permission tree – used to fully reset the tree UI
  const pristineWebTree = useRef([]);

  // ── react-hook-form ───────────────────────────────────────────────────────
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema[activeTab]), mode: "all" });

  // Register hidden "permission" field once
  useEffect(() => {
    register("permission");
  }, [register]);
const userAccessType = watch("User Acess Type", { web: false, mobile: false });
const prevAccessType = useRef({ web: undefined, mobile: undefined });
// In RoleRegistrationForm.js, update the useEffect that handles web toggling:
// In RoleRegistrationForm.js, update the useEffect that handles web/mobile toggling:

useEffect(() => {
  const accessType = userAccessType || {};
  const hasWeb = Boolean(accessType.web);
  const hasMobile = Boolean(accessType.mobile);
  const prev = prevAccessType.current;

  // Check if we're trying to turn OFF web access
  if (prev.web === true && !hasWeb) {
    const currentPermissions = getValues("permission");
    
    // If there are permissions selected, show validation error
    if (currentPermissions && currentPermissions.trim() !== "") {
      // Trigger validation to show the error message
      trigger("permission");
    } else {
      // No permissions, safe to disable
      console.log("Web toggled OFF - no permissions to clear");
    }
  }

  // Check if we're trying to turn OFF mobile access
  if (prev.mobile === true && !hasMobile) {
    const currentMobilePerms = getValues("Mobile_permission");
    
    // If there are permissions selected, show validation error
    if (currentMobilePerms && currentMobilePerms.length > 0) {
      // Trigger validation to show the error message
      trigger("Mobile_permission");
    } else {
      // No permissions, safe to disable
      console.log("Mobile toggled OFF - no permissions to clear");
      // clearMobilePermissions();
    }
  }

  prevAccessType.current = { web: hasWeb, mobile: hasMobile };

  setIsFieldDisabled({
    web_permission: !hasWeb,
    Web_access: !hasWeb,
    Mobile_permission: !hasMobile,
    Mobile_access: !hasMobile,
  });
}, [userAccessType, getValues, trigger]);
  // ── init data load ────────────────────────────────────────────────────────
  useEffect(() => {
    getAllUserPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getMobileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAllUserPermission = async () => {
    const permData = await getData(getAllPermissions);
    setPermissionDataFrmService(permData);

    const webPermissionData = permData.filter(
      (p) => p.menuType === "W" || !p.menuType
    );

    // Build hidden children map (3rd-level nodes)
    hiddenChildrenMap.current = {};
    const hiddenIds = new Set();

    webPermissionData.forEach((item) => {
      if (item.parent_id !== null) {
        const parent = webPermissionData.find((p) => p.id === item.parent_id);
        if (parent && parent.parent_id !== null) {
          hiddenIds.add(item.id);
          if (!hiddenChildrenMap.current[item.parent_id]) {
            hiddenChildrenMap.current[item.parent_id] = [];
          }
          hiddenChildrenMap.current[item.parent_id].push(item.id);
        }
      }
    });

    const filtered = webPermissionData.filter((item) => !hiddenIds.has(item.id));
    const hierarchy = flatToHier(filtered);

    // Store a pristine deep copy so we can fully reset the tree UI without re-fetching
    pristineWebTree.current = JSON.parse(JSON.stringify(hierarchy));

    // Update config immutably – never mutate CreateRoleConfig directly
    setRoleConfig((prev) =>
      prev.map((tab, tabIdx) => ({
        ...tab,
        fields: tab.fields.map((field, fieldIdx) =>
          tabIdx === 0 && fieldIdx === 2 ? { ...field, options: hierarchy } : field
        ),
      }))
    );
    setInitDataLoadComplete(true);
  };

  const getMobileData = () => {
    getData(getAllPermissionsMobile)
      .then((permData) => {
        const mobile = permData.filter((p) => p.menuType === "M");
        setPermissionDataFrmServiceMobile(mobile);
        setMobilePermissionsReady(true);

        const formattedOptions = mobile.map((p) => ({
          label: p.name,
          value: p.id,
          id: p.id,
        }));

        setRoleConfig((prev) =>
          prev.map((tab) => ({
            ...tab,
            fields: tab.fields.map((field) =>
              field.name === "Mobile_permission"
                ? { ...field, options: formattedOptions }
                : field
            ),
          }))
        );
      })
      .catch((err) => console.error("Error fetching mobile permissions:", err));
  };

  // ── populate edit data ────────────────────────────────────────────────────
  // Runs once both async loads complete (web + mobile) and we are in edit mode
  useEffect(() => {
    if (!initDataLoadComplete || !mobilePermissionsReady || !dataFrEdit) return;

    setValue("name", dataFrEdit.name);
    setValue("description", dataFrEdit.description);

    const userAccessTypes = dataFrEdit.userAccessType
      ? dataFrEdit.userAccessType.split(",")
      : [];
    setValue("User Acess Type", {
      web: userAccessTypes.includes("W"),
      mobile: userAccessTypes.includes("M"),
    });

    const adminValue = dataFrEdit.is_admin === true ? "true" : "false";
    setValue("Admin_Access", adminValue, { shouldValidate: true, shouldDirty: true });

    if (dataFrEdit.webPermissions) {
      const webPerms = dataFrEdit.webPermissions.split(",");
      setValue("Web_access", {
        view_permission: webPerms.includes("V"),
        edit_permission: webPerms.includes("E"),
        delete_permission: webPerms.includes("D"),
      });
    }

    if (dataFrEdit.mobPermissions) {
      const mobPerms = dataFrEdit.mobPermissions.split(",");
      setValue("Mobile_access", {
        view_permission: mobPerms.includes("V"),
        edit_permission: mobPerms.includes("E"),
        delete_permission: mobPerms.includes("D"),
      });
    }

    // Web tree permissions
    if (dataFrEdit.role_permission?.length) {
      const webPerms = dataFrEdit.role_permission.filter(
        (p) => p.userAccessType === "W" || !p.userAccessType
      );

      if (webPerms.length) {
        const webIds = webPerms.map((p) => p.permission_id);

        setRolePermissionData(webPerms.map((p) => ({ permission_id: p.permission_id })));
        setValue("permission", webIds.join(","));

        setRoleConfig((prev) =>
          prev.map((tab, tabIdx) => ({
            ...tab,
            fields: tab.fields.map((field, fieldIdx) => {
              if (tabIdx === 0 && fieldIdx === 2) {
                return {
                  ...field,
                  options: addSelectedFlag(field.options || [], webIds),
                };
              }
              return field;
            }),
          }))
        );
      }
    }

    // Mobile dropdown permissions
    setTimeout(() => {
      if (dataFrEdit.role_permission?.length && permissionDataFrmServiceMobile.length) {
        const mobilePermIds = dataFrEdit.role_permission
          .filter((p) => p.userAccessType === "M")
          .map((p) => p.permission_id);

        if (mobilePermIds.length) {
          const selected = permissionDataFrmServiceMobile
            .filter((p) => mobilePermIds.includes(p.id))
            .map((p) => ({ label: p.name, value: p.id, id: p.id }));
          setValue("Mobile_permission", selected);
        }
      }
    }, 300);
  }, [
    initDataLoadComplete,
    mobilePermissionsReady,
    dataFrEdit,
    permissionDataFrmServiceMobile,
    setValue,
  ]);

  // ── permission tree change handler ────────────────────────────────────────
  const onHierDropdownChange = useCallback(
    (currentNode, selectedNodes) => {
      const permissionArr = [];

      selectedNodes.forEach((node) => {
        permissionArr.push({ permission_id: node.id });

        // direct children (_children)
        (node._children || []).forEach((childId) => {
          permissionArr.push({ permission_id: childId });

          // hidden grandchildren
          (hiddenChildrenMap.current[childId] || []).forEach((gcId) => {
            permissionArr.push({ permission_id: gcId });
          });
        });

        // hidden children of selected node
        (hiddenChildrenMap.current[node.id] || []).forEach((hiddenId) => {
          permissionArr.push({ permission_id: hiddenId });
        });
      });

      // deduplicate
      const unique = Array.from(
        new Map(permissionArr.map((p) => [p.permission_id, p])).values()
      );

      setRolePermissionData(unique);
      setValue("permission", unique.map((p) => p.permission_id).join(","));
    },
    [setValue]
  );

  // ── keycloak helpers ──────────────────────────────────────────────────────
  const getLoggedInUserAuthToken = useCallback(
    () => keycloak.token,
    [keycloak]
  );

  const getURMHeader = useCallback(async () => {
    const token = await getLoggedInUserAuthToken();
    return {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }, [getLoggedInUserAuthToken]);

  const getRoleDataByRoleName = useCallback(
    async (roleName) => {
      const url =
        keycloakbaseurl +
        getRoleDataByRoleNameURL.replace(
          "{REALM}",
          process.env.REACT_APP_URM_REALM
        ) +
        roleName;
      const headers = await getURMHeader();
      const response = await api.getData(url, null, headers);
      return response.data;
    },
    [getURMHeader]
  );

  // ── app role CRUD ─────────────────────────────────────────────────────────
  const createRoleInApp = async (input) => {
    const payload = { ...input, status: true };
    delete payload.attributes;
    return postDataWithBody(createRole, payload, {});
  };

  const updateRoleInApp = async (input) => {
    const payload = { ...input, status: true };
    delete payload.attributes;
    return putDataWithBody(updateRole, payload, {});
  };

  // ── toast helpers ─────────────────────────────────────────────────────────
  const showSuccessMsg = (msg) =>
    toast.success(msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });

  const showErrorMsg = (msg) =>
    toast.error(msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });

  // ── assign permissions (generic) ──────────────────────────────────────────
  const assignPermissions = async (roleId, webPermissionsData, isUpdate) => {
    const mobilePerms = getValues("Mobile_permission") || [];
    const formattedPermissions = [];

    webPermissionsData.forEach((perm) => {
      formattedPermissions.push({ permission_id: perm.permission_id, userAccessType: "W" });
    });

    mobilePerms.forEach((perm) => {
      formattedPermissions.push({
        permission_id: perm.id || perm.value,
        userAccessType: "M",
      });
    });

    const roleObj = { id: roleId, role_permission: formattedPermissions };
    const res = await postDataWithBody(addPermissionsToRole + roleId, roleObj, {});

    if (res.status === "success") {
      showSuccessMsg(`Role ${isUpdate ? "updated" : "created"} successfully.`);
      navigate("../roles");
    } else {
      showErrorMsg(`An error occurred while ${isUpdate ? "updating" : "creating"} the role.`);
    }
  };

 const submitHandler = async (data, e) => {
  e.preventDefault();

  const accessTypeString = Object.keys(data["User Acess Type"] || {})
    .filter((key) => data["User Acess Type"][key])
    .map((key) => (key === "web" ? "W" : key === "mobile" ? "M" : ""))
    .join(",");

  const buildPermString = (access) => {
    const parts = [];
    if (access?.view_permission) parts.push("V");
    if (access?.edit_permission) parts.push("E");
    if (access?.delete_permission) parts.push("D");
    return parts.join(",");
  };

  const inputobj = {
    name: data.name,
    description: data.description,
    attributes: { created_on: [new Date().getTime()] },
    userAccessType: accessTypeString,
    webPermissions: buildPermString(data.Web_access),
    mobPermissions: buildPermString(data.Mobile_access),
    is_admin: data.Admin_Access === "true",
  };

  // Check if web access is enabled before including web permissions
  const hasWebAccess = data["User Acess Type"]?.web || false;
  
  // Only include web permissions if web access is enabled
  let currentWebPermissions = [];
  
  if (hasWebAccess) {
    const permissionString = getValues("permission");
    
    if (permissionString) {
      currentWebPermissions = permissionString
        .split(",")
        .filter(Boolean)
        .map((id) => ({ permission_id: id }));
      setRolePermissionData(currentWebPermissions);
    } else if (dataFrEdit?.role_permission && hasWebAccess) {
      currentWebPermissions = dataFrEdit.role_permission
        .filter((p) => p.userAccessType === "W" || !p.userAccessType)
        .map((p) => ({ permission_id: p.permission_id }));
    }
  } else {
    // Explicitly clear web permissions when web access is disabled
    setRolePermissionData([]);
    setValue("permission", "");
  }

  if (dataFrEdit) {
    // UPDATE flow
    inputobj.id = dataFrEdit.id;

    const delRes = await putDataWithBody(
      deletePermissionsFromRole + dataFrEdit.id,
      {},
      {}
    );

    if (delRes.status === "success") {
      const updRes = await updateRoleInApp(inputobj);
      if (updRes.id) {
        await assignPermissions(dataFrEdit.id, currentWebPermissions, true);
      }
    }
  } else {
    // CREATE flow
    const url =
      keycloakbaseurl +
      createRoleintoRealm.replace("{REALM}", process.env.REACT_APP_URM_REALM);
    const headers = await getURMHeader();

    try {
      const response = await api.postData(
        url,
        headers,
        JSON.stringify({
          name: inputobj.name,
          description: inputobj.description,
          attributes: inputobj.attributes,
        })
      );

      if (response.status === 201) {
        const roleobj = await getRoleDataByRoleName(data.name);
        inputobj.urm_role_id = roleobj.id;
        const createRes = await createRoleInApp(inputobj);
        if (createRes.id) {
          await assignPermissions(createRes.id, currentWebPermissions, false);
        }
      }
    } catch (err) {
      showErrorMsg(err.response?.data?.errorMessage || "An error occurred");
    }
  }
};
  // ── form render helpers ───────────────────────────────────────────────────
  const GenerateForm = useCallback(
    (fields) => (
      <div>
        {initDataLoadComplete && (
          <FormHandler
            register={register}
            errors={errors}
            fields={fields}
            onChange={onHierDropdownChange}
            control={control}
            columns={2}
            getValues={getValues}
            webaccess={userAccessType?.mobile}
            mobileAccess={userAccessType?.web}
            isFieldDisabled={isFieldDisabled}
            webTreeKey={webTreeKey}
          />
        )}
      </div>
    ),
    [
      initDataLoadComplete,
      register,
      errors,
      onHierDropdownChange,
      control,
      getValues,
      userAccessType,
      isFieldDisabled,
      webTreeKey,
    ]
  );

  const tabsData = roleConfig.map((tab) => ({
    ...tab,
    component: GenerateForm(tab.fields),
  }));

  // ── tab navigation ────────────────────────────────────────────────────────
  const saveTabDataHandler = async () => {
    const isStepValid = await trigger();
    if (isStepValid) setActiveTab((prev) => prev + 1);
  };

  const previousHandler = () => {
    if (activeTab > 0) setActiveTab((prev) => prev - 1);
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="mt-4">
      <ToastContainer />
      <div className="form-body">
        <div className="registration__form--body">
          <form onSubmit={handleSubmit(submitHandler)} name="AgancyRegForm">
            <div>
              {tabsData.map((tab) =>
                tab.id === activeTab ? (
                  <React.Fragment key={tab.id}>
                    <div className="registration__form--heading">{tab.heading}</div>
                    <div>{tab.component}</div>
                  </React.Fragment>
                ) : null
              )}
            </div>

            <div className="d-flex justify-content-center">
              {activeTab > 0 && (
                <Button
                  variant="secondary"
                  className="previousbutton-style mx-2"
                  onClick={previousHandler}
                >
                  Previous
                </Button>
              )}
              {tabsData.length - 1 === activeTab ? (
                <Button type="submit" className="mx-2" variant="primary">
                  {t("saveAndSubmit")}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  className="mx-2"
                  onClick={saveTabDataHandler}
                >
                  Save & Next
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleRegistrationForm;